import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import { App } from "antd";
import { CALENDARS, LOADING_CALENDAR } from "../store/selectors";
import {
  setLoading,
  setCalendar,
  updateWeekInCalendar,
} from "../store/slices/calendar";
import {
  getCalendar,
  initializeCalendar,
  updateSingleWeekDate,
  updateMultipleWeekDates,
} from "../services/calendar/calendar.service";
import {
  EducationLevel,
  InitializeCalendarRequest,
  UpdateWeekDateRequest,
  UpdateMultipleWeeksRequest,
} from "../services/calendar/calendar.types";

/**
 * General (Emmi-wide) calendars, one per education level (SCRUM-744). Every
 * read and write carries its `EducationLevel`, so the two calendars never mix:
 * the store keeps them side by side and each action only touches its own level.
 */
export const useCalendar = () => {
  // From App.useApp(): the static notification does not render under React 19.
  const { notification } = App.useApp();
  const dispatch = useDispatch();
  const calendars = useSelector(CALENDARS);
  const loading = useSelector(LOADING_CALENDAR);

  const fetchCalendar = useCallback(
    async (level: EducationLevel) => {
      dispatch(setLoading(true));
      try {
        const calendarResponse = await getCalendar(level);
        dispatch(setCalendar({ level, calendar: calendarResponse }));
      } catch (error: any) {
        dispatch(setLoading(false));
        notification["error"]({
          message: "Error",
          description:
            "No se pudo cargar el calendario. Por favor, intenta nuevamente.",
          placement: "top",
          duration: 3,
        });
        console.error("Error fetching calendar:", error);
      }
    },
    [dispatch, notification]
  );

  const initCalendar = useCallback(
    async (level: EducationLevel, data: InitializeCalendarRequest) => {
      dispatch(setLoading(true));
      try {
        await initializeCalendar({ ...data, education_level: level });
        await fetchCalendar(level); // Refrescar el calendario después de inicializar
        notification["success"]({
          message: "Éxito",
          description: "Calendario inicializado correctamente",
          placement: "top",
          duration: 3,
        });
      } catch (error: any) {
        dispatch(setLoading(false));
        const errorMessage =
          error.response?.data?.message || "Error al inicializar el calendario";
        notification["error"]({
          message: "Error",
          description: errorMessage,
          placement: "top",
          duration: 3,
        });
        console.error("Error initializing calendar:", error);
      }
    },
    [dispatch, fetchCalendar, notification]
  );

  const updateWeekDateHandler = useCallback(
    async (
      level: EducationLevel,
      data: UpdateWeekDateRequest | UpdateMultipleWeeksRequest
    ) => {
      dispatch(setLoading(true));
      try {
        // Determine which function to call based on the data structure
        const updatedCalendar =
          "weeks" in data
            ? await updateMultipleWeekDates({
                ...(data as UpdateMultipleWeeksRequest),
                education_level: level,
              })
            : await updateSingleWeekDate({
                ...(data as UpdateWeekDateRequest),
                education_level: level,
              });

        dispatch(updateWeekInCalendar({ level, calendar: updatedCalendar }));
        notification["success"]({
          message: "Éxito",
          description: "Fecha de semana actualizada correctamente",
          placement: "top",
          duration: 3,
        });
      } catch (error: any) {
        dispatch(setLoading(false));
        const errorMessage =
          error.response?.data?.message ||
          "Error al actualizar la fecha de la semana";

        // Manejo especial para el error de solapamiento
        if (errorMessage.includes("ya tiene contenido semanal")) {
          notification["warning"]({
            message: "Fecha no disponible",
            description:
              "Esa fecha ya tiene contenido semanal para desbloquear asignado. Elige otra fecha.",
            placement: "top",
            duration: 5,
          });
        } else {
          notification["error"]({
            message: "Error",
            description: errorMessage,
            placement: "top",
            duration: 3,
          });
        }
        console.error("Error updating week date:", error);
        throw error;
      }
    },
    [dispatch, notification]
  );

  return {
    calendars,
    loading,
    fetchCalendar,
    initCalendar,
    updateWeekDate: updateWeekDateHandler,
  };
};
